import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { Navigate, Route } from 'react-router';
import type { Role } from '@gitrader/shared';
import { Spinner } from '../components/ui';
import PlaceholderPage from '../pages/PlaceholderPage';
import { LiveGate } from '../shell/live/LiveGate';
import { allowed, firstLeaf, flatten, type ResolvedRoute } from './helpers';
import { ROUTE_TREE } from './tree';
import type { RouteNode } from './types';

// One lazy component per node, created once — recreating it per render would remount the page.
const lazyPages = new Map<string, LazyExoticComponent<ComponentType>>();
function pageFor(node: RouteNode): ComponentType {
  if (!node.page) return PlaceholderPage;
  let page = lazyPages.get(node.id);
  if (!page) {
    page = lazy(node.page);
    lazyPages.set(node.id, page);
  }
  return page;
}

export function PageFallback() {
  return (
    <div className="flex items-center justify-center py-16 text-ink-3" aria-busy="true">
      <Spinner />
    </div>
  );
}

/**
 * The element for one resolved route.
 *  - below minRole: redirected, never rendered (display concern; the API enforces on its own)
 *  - a section (children, no page): redirected to its first visible leaf
 *  - a leaf, or a node with a page and sub-routes: its page (or the Placeholder), inside the
 *    LiveGate when env is live
 */
export function RouteEntry({ route, role }: { route: ResolvedRoute; role: Role }) {
  if (!allowed(route, role)) return <Navigate to="/" replace />;
  // A section without a page of its own lands on its first leaf. A node that has BOTH a page
  // and children (Jobs → Jobs/:id) renders its page; the children are its sub-routes.
  if (route.node.children?.length && !route.node.page) {
    const leaf = firstLeaf(route, role);
    return <Navigate to={leaf?.fullPath ?? '/'} replace />;
  }
  const Page = pageFor(route.node);
  const content = (
    <Suspense fallback={<PageFallback />}>
      <Page />
    </Suspense>
  );
  return route.env === 'live' ? <LiveGate>{content}</LiveGate> : content;
}

/** <Route> elements for the whole tree — the router reads the same data as the nav. */
export function treeRoutes(role: Role) {
  return flatten(ROUTE_TREE).map((route) => (
    <Route
      key={route.node.id}
      path={route.fullPath}
      element={<RouteEntry route={route} role={role} />}
    />
  ));
}
